import { Text } from 'tio-ui/components/typography';
import { eq } from 'tio-ui/utilities';
import { get } from '@ember/helper';
import type { TOC } from '@ember/component/template-only';

// ---------------------------------------------------------------------------
// PipelineStage – a single stage in a pipeline flow visualization
// ---------------------------------------------------------------------------

export type PipelineStageColor = 'violet' | 'teal' | 'green' | 'gray' | 'amber';
export type PipelineStageSize = 'sm' | 'md' | 'lg';

interface PipelineStageSignature {
  Args: {
    /** The numeric or string value to display */
    value: string | number;
    /** Label text below the value */
    label: string;
    /** Color theme for the stage */
    color?: PipelineStageColor;
    /** Size variant - sm for compact, md for default, lg for bordered */
    size?: PipelineStageSize;
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<PipelineStageColor, { bg: string; text: string; border: string }> = {
  violet: {
    bg: 'bg-violet-50 dark:bg-violet-900/30',
    text: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-700',
  },
  teal: {
    bg: 'bg-teal-50 dark:bg-teal-900/30',
    text: 'text-teal-600 dark:text-teal-400',
    border: 'border-teal-200 dark:border-teal-700',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-700',
  },
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-200',
    border: 'border-gray-200 dark:border-gray-600',
  },
  amber: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-700',
  },
};

const sizeClasses: Record<PipelineStageSize, string> = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'px-8 py-4 border-2',
};

/**
 * PipelineStage - A single stage in a pipeline flow visualization
 *
 * Displays a value and label in a colored box, typically used within a
 * horizontal flow with arrows between stages.
 *
 * @example
 * ```gts
 * import PipelineStage from 'tio-reimagined/components/pipeline-stage';
 *
 * <PipelineStage @value={{85}} @label="In Pipeline" @color="violet" />
 * ```
 */
const PipelineStage: TOC<PipelineStageSignature> = <template>
  {{#let (if @color @color "violet") as |color|}}
    {{#let (if @size @size "md") as |size|}}
      {{#let (get colorClasses color) as |colors|}}
        <div
          data-test-pipeline-stage
          class="text-center rounded-lg
            {{get sizeClasses size}}
            {{colors.bg}}
            {{if (eq size 'lg') colors.border}}"
          ...attributes
        >
          <div data-test-pipeline-stage-value class="text-3xl font-bold {{colors.text}}">
            {{@value}}
          </div>
          <Text data-test-pipeline-stage-label @intent="muted" @size="sm">{{@label}}</Text>
        </div>
      {{/let}}
    {{/let}}
  {{/let}}
</template>;

export default PipelineStage;

export type { PipelineStageSignature };
